import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { TicketKey } from 'flow/types/EntityKeys';
import { TicketsSearchParams } from 'flow/types/Tickets';

import {
    selectApiHasMoreTicketsSearchedData,
    selectKeysTicketsSearched,
} from 'flow/selectors/TicketsSelectors';

import {
    executeTicketsSearchLatest,
    executeTicketsSearchNext,
} from 'flow/actions/TicketsSearchAction';

import TicketsList from 'flow/components/tickets/tickets-list/TicketsList';

interface Props {
    itemSelectedKey?: TicketKey;
    scrollableTargetId: string;
    listItemClicked: (key: TicketKey) => void;
    ticketsSearchParams: TicketsSearchParams;
    context: 'MY_TICKETS' | 'UNASSIGNED' | 'ALL' | 'ADVANCED_SEARCH';
}

const TicketsListSearchResultWrapper = ({
    itemSelectedKey,
    scrollableTargetId,
    listItemClicked,
    ticketsSearchParams,
    context,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const [isFetchingLatestData, setIsFetchingLatestData] = useState(false);
    const [searchFailed, setSearchFailed] = useState(false);

    const apiHasMoreData = useAppSelector(selectApiHasMoreTicketsSearchedData);
    const itemKeysList = useAppSelector(selectKeysTicketsSearched);

    useEffect(() => {
        // A newer search replaces this one. Its loading state must not end
        // when this older request settles.
        let isCurrentSearch = true;
        setIsFetchingLatestData(true);
        setSearchFailed(false);
        dispatch(executeTicketsSearchLatest(ticketsSearchParams, context))
            .catch(() => {
                // The store still holds the results of the previous search,
                // maybe from another pill. Do not show them as these results.
                if (isCurrentSearch) setSearchFailed(true);
            })
            .finally(() => {
                if (isCurrentSearch) setIsFetchingLatestData(false);
            });
        return () => {
            isCurrentSearch = false;
        };
    }, [context, dispatch, ticketsSearchParams]);

    // A failed next page keeps the loaded pages. The action shows the
    // network error.
    const fetchNext = useCallback(
        () =>
            dispatch(
                executeTicketsSearchNext(ticketsSearchParams, context),
            ).catch((): void => undefined),
        [context, dispatch, ticketsSearchParams],
    );

    const showError = searchFailed && !isFetchingLatestData;

    const getResults = () => {
        if (showError) return null;
        if (itemKeysList.isEmpty() && !isFetchingLatestData) {
            return (
                <div className="flex flex-col items-center justify-center gap-y-4 p-8 text-center">
                    <span className="text-xl font-bold">
                        {i18next.t('general.search.no-results', {
                            search: ticketsSearchParams.searchQuery,
                        })}
                    </span>
                    <span className="text-gray-800">
                        {`${i18next.t('search.try_search_by')} ${i18next.t('Generics.PhoneNumber').toLowerCase()}, ${i18next.t('ticket.subject').toLowerCase()}, ${i18next.t('Generics.or')} ${i18next.t('tickets.ticket.id').toLowerCase()}.`}
                    </span>
                </div>
            );
        }
        return (
            <TicketsList
                key={`tlsrw-${context}`}
                itemKeys={itemKeysList}
                handleListItemClicked={listItemClicked}
                itemSelectedKey={itemSelectedKey}
                displayLoadingItem={isFetchingLatestData}
                onReachedEndOfList={fetchNext}
                canFetchMoreData={apiHasMoreData}
                scrollableTargetId={scrollableTargetId}
            />
        );
    };

    return (
        <>
            {/* The live region stays mounted, so a screen reader announces
                the error when its text arrives. A region inserted together
                with its text is often not read. */}
            <div
                role="alert"
                className={
                    showError
                        ? 'flex flex-col items-center justify-center gap-y-4 p-8 text-center'
                        : undefined
                }
            >
                {showError ? (
                    <span className="text-xl font-bold">
                        {i18next.t('snackbar.error')}
                    </span>
                ) : null}
            </div>
            {getResults()}
        </>
    );
};

export default React.memo(TicketsListSearchResultWrapper);
